<template>
    <div class="h-screen">
        <template v-if="responsive">
            <MobileNavBar class="flex lg:hidden" :class="[`nav-theme-${theme}`]">
                <template #logo>
                    <slot name="logo" />
                </template>
                <template #avatar>
                    <slot name="avatar" />
                </template>
            </MobileNavBar>
        </template>
        <div
            class="patsnap-biz-ai-history-nav h-full"
            :style="{ width: internalOpened ? `${props.navWidth}px` : `${props.navMiniWidth}px` }"
            :class="[
                internalOpened ? 'patsnap-biz-ai-history-nav-wide' : 'patsnap-biz-ai-history-nav-collapse cursor-e-resize',
                `nav-theme-${theme}`,
                responsive ? 'hidden lg:flex lg:flex-col' : 'flex flex-col',
            ]"
            @click="handleNavClick"
        >
            <slot name="productLogo" :opened="internalOpened" />
            <div class="flex flex-col flex-1 overflow-hidden" :class="{ 'items-start': internalOpened, 'items-center': !internalOpened }">
                <div class="w-full flex flex-col">
                    <MenuItem
                        v-for="menu in menus"
                        :key="menu.id"
                        :opened="internalOpened"
                        :menu="menu"
                        :expand="expandMenuId === menu.id"
                        @expand="handleExpandMenu"
                    />
                    <div
                        v-if="internalOpened && injectIsLogin"
                        class="flex flex-row items-center gap-2 py-2 h-10 px-4 mt-4 cursor-pointer hover:bg-gray-hover"
                        @click="handleExpandHistory"
                        @mouseenter="showHistoryTriangle = true"
                        @mouseleave="showHistoryTriangle = false"
                    >
                        <span class="text-xs opacity-60 text-white">{{ historyMenu.title }}</span>
                        <PtIcon v-show="!historyExpanded || showHistoryTriangle" class="shrink-0 text-xl fill-white opacity-60" :icon="historyExpanded ? 'SolidArrowDown' : 'SolidArrowRight' " />
                    </div>
                </div>
                <div v-if="historyVisible" class="ai-history-nav__smooth-scroll patsnap-biz-ai-history-nav-list overflow-x-hidden">
                    <HistorySubItem
                        v-for="child in filteredHistoryChildren"
                        :key="child.id"
                        :child="child"
                    />
                    <MenuItem
                        v-if="filteredHistoryChildren.length > 5"
                        :opened="internalOpened"
                        :menu="viewAllHistoryItem"
                        @click="$emit('clickViewAllHistory', viewAllHistoryItem)"
                    />
                    <slot name="historyFooter" />
                </div>
                <MenuItem v-else-if="!internalOpened && injectIsLogin" :opened="internalOpened" :menu="historyMenu">
                    <template #footer>
                        <a
                            class="px-4 py-2 h-6 box-content flex items-center justify-start hover:underline text-eureka-600 cursor-pointer"
                            @click="$emit('clickViewAllHistory', viewAllHistoryItem)"
                        >
                            {{ t('NavBar.historyViewAll') }}
                        </a>
                    </template>
                </MenuItem>
            </div>

            <slot name="avatar" :opened="internalOpened" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, provide, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import _ from 'lodash';
// import { ElTooltip } from 'element-plus';
import PtIcon from '@patsnap-biz/icon';
// import ToggleIcon from './components/icons/ToggleIcon.vue';
import MenuItem from './components/MenuItem.vue';
import HistorySubItem from './components/HistorySubItem.vue';
import { type IMenuItem, NAV_MENU_TYPE } from './types';
import { IMenuType } from './types';
import { NAV_LIST_CLASS } from './constants';
import MobileNavBar from './MobileNavBar.vue';
import '@patsnap-ui/icon/assets/solid/ArrowSmallUp.svg';
import '@patsnap-ui/icon/assets/solid/TimeRefresh.svg';
import '@patsnap-ui/icon/assets/solid/ArrowDown.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import {
    cn, en, jp, tw,
} from './locales';
import './style/index.js';
import { EUREKA_URI } from '../../domain-config/config';


const { t } = useI18n({
    messages: {
        cn, en, tw, jp,
    },
});

const props = withDefaults(defineProps<{
    modelValue: boolean,
    menus: IMenuItem[];
    historyMenu: IMenuItem;
    messages?: unknown;
    closeText?: string;
    openText?: string;
    /**
     * nav宽度
     */
    navWidth?: number;
    /**
     * nav收起宽度
     */
    navMiniWidth?: number;
    /**
     * 主题色
     */
    theme: 'light' | 'dark',
    /**
     * 是否启动响应式
     */
    responsive?: boolean;
}>(), {
    navWidth: 220,
    navMiniWidth: 64,
    theme: 'dark',
    responsive: true,
});

const injectIsLogin = inject('isLogin', null);

const emit = defineEmits(['clickHistory', 'removeHistory', 'clickViewAllHistory', 'update:modelValue']);

// 允许显示的历史记录类型
const allowedTypes = [
    IMenuType.IP_Hiro,
    IMenuType.IP_TELECOM_AICC,
    IMenuType.AI_FTO_IMAGE,
    IMenuType.AI_NOVELTY_SEARCH,
    IMenuType.AI_NEW_FTO_SEARCH,
    IMenuType.AI_FTO_SEARCH,
];

const viewAllHistoryItem = computed(() => {
    return {
        id: 'HISTORY_VIEW_ALL',
        title: t('NavBar.historyViewAll'),
        link: `${EUREKA_URI}/history`,
        type: NAV_MENU_TYPE.HISTORY,
    };
});

const isListScrollable = ref(false);

const expandMenuId = ref('');

const internalOpened = ref(props.modelValue);

// 历史记录独立的展开状态
const historyExpanded = ref(true);

// 过滤后的历史记录
const filteredHistoryChildren = computed(() => {
    if (!props.historyMenu?.children) return [];
    return props.historyMenu.children.filter(child => {
        // 如果没有 type 字段，则保留（兼容旧数据）
        if (!child.type) return true;
        return true;
        // 只保留允许的类型
        // return allowedTypes.includes(child.type);
    });
});

const total = computed(() => _.size(filteredHistoryChildren.value));

// Triangle 显示状态
const showHistoryTriangle = ref(false);

// const expandText = computed(() => props.openText || t('AIHistoryNav.open'));

// const hideText = computed(() => props.closeText || t('AIHistoryNav.close'));

const historyVisible = computed(() => total.value && internalOpened.value && historyExpanded.value);

watch(
    () => props.modelValue,
    (newVal) => {
        internalOpened.value = newVal;
    },
);

function isScrollable(element: HTMLElement) {
    return element.scrollHeight > element.clientHeight;
}

watch(() => total.value, () => {
    const navListEle = document.querySelector(NAV_LIST_CLASS) as HTMLElement;
    if (navListEle) {
        isListScrollable.value = isScrollable(navListEle);
    }
}, {
    immediate: true,
    flush: 'post',
});

function findActiveMenu(children: IMenuItem[] = []): IMenuItem | undefined {
    return _.find(children, (child) => child.active) as IMenuItem | undefined;
}
function setAutoExpandMenu() {
    const historyActiveMenu = findActiveMenu(props.historyMenu?.children);
    if (historyActiveMenu) {
        expandMenuId.value = props.historyMenu.id;
        return;
    }

    const activeMenu = _.find(props.menus, (menu) => findActiveMenu(menu?.children)) as IMenuItem | undefined;
    if (activeMenu) {
        expandMenuId.value = activeMenu.id;
        return;
    }

    if (!expandMenuId.value) {
        expandMenuId.value = _.get(
            _.find(props.menus, (menu) => menu.expand),
            'id',
            _.get(props.menus, '[1].id')!,
        ) as string;
    }
}

watch(() => props.historyMenu?.children?.length, () => {
    setAutoExpandMenu();
}, {
    immediate: true,
});

function handleExpandMenu(menuId: string) {
    expandMenuId.value = expandMenuId.value === menuId ? '' : menuId;
}

function handleNavClick(event: MouseEvent) {
    if (internalOpened.value) return;
    const target = event.target as HTMLElement;
    const interactive = target.closest('a, button, [role="button"], .cursor-pointer, .patsnap-biz-nav-header-dropdown, .th-btn');
    if (!interactive) {
        emit('update:modelValue', true);
    }
}

function handleExpandHistory() {
    historyExpanded.value = !historyExpanded.value;
}

function removeHistoryItem(item: IMenuItem) {
    emit('removeHistory', item);
}

function clickHistoryItem(item: IMenuItem) {
    emit('clickHistory', item);
}

provide('removeHistory', removeHistoryItem);
provide('clickHistory', clickHistoryItem);
provide('messages', props.messages);

onMounted(() => {

});
</script>

<style lang="scss">
.patsnap-biz-ai-history-nav {
    background-color: var(--nav-bg-color);
    .ai-history-nav__smooth-scroll {
        overflow-y: auto;
        scrollbar-color: transparent transparent;

        /* 滑块样式 */
        ::-webkit-scrollbar-thumb {
            background-color: transparent;
        }

        &:hover {
            scrollbar-color: rgba(0, 0, 0, 0.1) transparent;

            &::-webkit-scrollbar-thumb {
                background-color: rgba(0, 0, 0, 0.1);
            }
        }
    }

    .active-menu-item {
        @apply bg-gray-hover;
        &::after {
            content: '';
            background: var(--nav-bg-secondary-color);
            @apply absolute right-0 w-1 h-full;
        }
    }
    .nav-toggle-container, .patsnap-biz-ai-history-item {
        color: var(--nav-text-color);
        .patsnap-biz-icon, .nav-toggle-icon {
            fill: var(--nav-fill-color);
        }
    }
    .nav-history-sub-item {
        color: var(--nav-text-third-color);
        .patsnap-biz-icon {
            fill: var(--nav-fill-color);
        }
    }
    .menu-divide-top-line {
        @apply relative;
        &::before {
            content: '';
            @apply absolute top-0 left-3 right-3 h-[1px] bg-white/20;
        }
    }
    .menu-divide-bottom-line {
        @apply relative;
        &::after {
            content: '';
            background-color: var( --nav-divider-color);
            @apply absolute bottom-0 left-3 right-3 h-[1px];
        }
    }
}
</style>
